import React from 'react';

/**
 * Barra de progresso do design system (at-67, frente D/D5).
 *
 * 🔴 **Nasceu por PROMOÇÃO, não do zero.** O corpo veio de
 * `components/vistoria-progress/VistoriaUploadProgress.tsx`, que a at-63 deixou
 * fora do kit com uma justificativa explícita: *"tem UM consumidor… a regra do kit
 * é que primitivo sem consumidor plural não nasce"*. **O plural chegou nesta
 * branch**: os modais de Sincronizar Cotas, Sincronizar Produtos e Descoberta de
 * Associados desenhavam a própria barra com `style={{}}` e cores hardcoded
 * (`#f97316`, `#16a34a`, `#dc2626` — e um `#2563eb` que não existe nos tokens).
 * Com a captura da vistoria, são **4 consumidores**.
 *
 * ⚠️ **O percentual é responsabilidade de quem usa** — aqui ele só é normalizado
 * (`NaN` vira 0; fora de 0–100 é limitado) e desenhado. 📌 Na vistoria o progresso
 * é honesto porque quem o alimenta conta as RESPOSTAS do backend, não o
 * `onUploadProgress` do axios (at-24); nos syncs, porque vem do `percentual` que a
 * run persiste no banco.
 *
 * ⚠️ **`rotulo` é OPCIONAL** — é a única diferença de contrato em relação ao
 * componente de origem. Na vistoria o rótulo é a própria informação ("Enviando
 * foto 3 de 29..."); nos modais de sincronização o texto de estado vive no
 * cabeçalho do `PainelExecucaoSync`, e repeti-lo aqui seria dizer duas vezes a
 * mesma coisa. Sem rótulo, a barra fica sozinha na linha, com o percentual à
 * direita.
 */

export type EstadoBarraProgresso = 'em-andamento' | 'erro' | 'concluido';

const COR_PREENCHIMENTO: Record<EstadoBarraProgresso, string> = {
  'em-andamento': 'tw-bg-brand',
  erro: 'tw-bg-danger',
  concluido: 'tw-bg-success',
};

const COR_PERCENTUAL: Record<EstadoBarraProgresso, string> = {
  'em-andamento': 'tw-text-brand',
  erro: 'tw-text-danger',
  concluido: 'tw-text-success',
};

interface PropsBarraProgresso {
  /** Percentual real do trabalho concluído (0 a 100). */
  percent: number;
  /**
   * Texto principal, à esquerda do percentual. Opcional — ver o aviso no topo.
   *
   * ⚠️ Quando presente, vira também o `aria-label` da barra.
   */
  rotulo?: string;
  estado?: EstadoBarraProgresso;
  /** Linha auxiliar abaixo da barra (ex.: instrução de retry). */
  detalhe?: string;
  /**
   * Desenha só a barra, sem a moldura (borda + fundo + padding).
   *
   * 🔴 Existe para a barra do **histórico da descoberta**, que fica dentro de um
   * bloco que já tem moldura própria — aninhar duas caixas ali produz uma borda
   * dupla. Sem esta prop, o consumidor recriaria a barra à mão, que é justamente
   * o que este componente veio impedir.
   */
  semMoldura?: boolean;
  className?: string;
}

const BarraProgresso: React.FC<PropsBarraProgresso> = ({
  percent,
  rotulo,
  estado = 'em-andamento',
  detalhe,
  semMoldura = false,
  className = '',
}) => {
  const normalizado = Number.isFinite(percent) ? percent : 0;
  const limitado = Math.max(0, Math.min(100, normalizado));
  const arredondado = Math.round(limitado);

  return (
    /* ⚠️ **Sem margem própria**: quem espaça é o container (mesma filosofia do
       `SearchInput`, que não força a própria largura). Na vistoria ela vive dentro
       da faixa `sticky` do `FormActions`, cujo `gap` já cuida disso. */
    <div
      className={[
        semMoldura
          ? ''
          : 'tw-rounded-xl tw-border tw-border-superficie-borda tw-bg-superficie tw-p-3',
        className,
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <div className="tw-mb-2 tw-flex tw-items-baseline tw-justify-between tw-gap-3">
        {/*
          `aria-live="polite"` só no TEXTO DE ESTADO — nunca no percentual.
          O rótulo muda uma vez por foto ("Enviando foto 3 de 29..."), o que é
          progresso útil de ouvir; o percentual muda continuamente e faria o
          leitor de tela falar sem parar.
        */}
        {rotulo ? (
          <span className="tw-text-xs tw-font-semibold tw-text-texto-corpo" aria-live="polite">
            {rotulo}
          </span>
        ) : (
          <span aria-hidden="true" />
        )}
        <span className={`tw-shrink-0 tw-text-xs tw-font-bold ${COR_PERCENTUAL[estado]}`}>
          {arredondado}%
        </span>
      </div>

      <div
        className="tw-h-2 tw-w-full tw-overflow-hidden tw-rounded-full tw-bg-superficie-borda"
        role="progressbar"
        aria-label={rotulo ?? 'Progresso'}
        aria-valuenow={arredondado}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className={`tw-h-full tw-rounded-full tw-transition-all ${COR_PREENCHIMENTO[estado]}`}
          style={{ width: `${limitado}%` }}
        />
      </div>

      {detalhe && <p className="tw-mb-0 tw-mt-2 tw-text-xs tw-text-texto-suave">{detalhe}</p>}
    </div>
  );
};

export default BarraProgresso;
